"use client";

import { useMemo } from 'react'
import { Bar, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid, ReferenceLine } from 'recharts'
import { useFinance, useTagPolicy, isIncome, isSpending, isExcludedByTag } from '@/lib/finance/finance-data-context'
import { computeSavings, type SavingsYear } from '@/lib/finance/savings'
import { isRentalPropertyAccount } from '@/lib/finance/aggregate'
import { fmtUsd, fmtUsdCompact, fmtPct } from '@/lib/finance/format'
import { Card, PageHeader, StatTile, LegendKey } from '@/components/finance/ui'
import { ChartTooltip, barCursor } from '@/components/finance/ChartTooltip'
import { xAxisProps, yAxisProps, gridProps, BAR_MAX, BAR_RADIUS, CHART_MARGIN, ACTIVE_DOT } from '@/components/finance/chartDefaults'

/**
 * Savings — each year's change in TOTAL net worth split into what we put in
 * (saved income, home mortgage principal, retirement contributions, the
 * rental property as one unit) and what the market did (property marks,
 * market & other). See lib/finance/savings.ts for the identity.
 */
const SERIES: { key: keyof SavingsYear; label: string; color: string }[] = [
  { key: 'savedIncome', label: 'Saved income', color: 'var(--series-2)' },
  { key: 'mortgagePrincipal', label: 'Mortgage principal', color: 'var(--series-4)' },
  { key: 'retirement', label: 'Retirement contributions', color: 'var(--series-1)' },
  { key: 'rental', label: 'Rental property', color: 'var(--series-9)' },
  { key: 'propertyMarks', label: 'Property marks', color: 'var(--series-8)' },
  { key: 'marketOther', label: 'Market & other', color: 'var(--series-3)' },
]

const yearLabel = (r: SavingsYear) => (r.ytd ? `${r.year} YTD` : String(r.year))
const signed = (v: number) => (v >= 0 ? 'text-pos' : 'text-neg')

export function Savings() {
  const { accounts, networth, txns: txnsOrNull } = useFinance()
  const txns = useMemo(() => txnsOrNull ?? [], [txnsOrNull])
  const { policy } = useTagPolicy()

  const years = useMemo(
    () =>
      computeSavings(
        { accounts, networth, txns },
        {
          isRental: isRentalPropertyAccount,
          classify: (t) =>
            isIncome(t, policy)
              ? 'income'
              : isSpending(t, policy)
                ? 'spending'
                : t.budgetable && (t.rollup === 'Income' || t.rollup === 'Expense') && isExcludedByTag(t, policy)
                  ? 'excluded'
                  : null,
        },
      ).filter((r) => r.increase != null),
    [accounts, networth, txns, policy],
  )
  const latest = years.at(-1) ?? null
  const rows = useMemo(() => years.map((r) => ({ ...r, label: yearLabel(r) })), [years])
  const byYearDesc = useMemo(() => [...years].reverse(), [years])
  // A line only earns a bar/column when it carries a value in some year.
  const shown = useMemo(
    () => SERIES.filter((s) => s.key === 'marketOther' || years.some((r) => ((r[s.key] as number | null) ?? 0) !== 0)),
    [years],
  )
  const has = (k: keyof SavingsYear) => shown.some((s) => s.key === k)

  return (
    <div className="space-y-4">
      <PageHeader title="Savings" />

      {latest && (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <StatTile label={`Put in · ${yearLabel(latest)}`} value={fmtUsdCompact(latest.saved)} delta={latest.saved} deltaLabel="" />
          <StatTile label="Capital appreciation" value={fmtUsdCompact(latest.capital ?? 0)} delta={latest.capital ?? 0} deltaLabel="" />
          <StatTile label="Net worth change" value={fmtUsdCompact(latest.increase ?? 0)} delta={latest.increase ?? 0} deltaLabel="" />
          <StatTile label="Capital share of change" value={latest.capitalShare == null ? '—' : fmtPct(latest.capitalShare)} />
        </div>
      )}

      <Card
        title="Where each year's net worth change came from"
        subtitle="Bars stack to the change in net worth · saved income is income − spending under the current tag policy"
      >
        <div className="mb-2 flex flex-wrap gap-3">
          {shown.map((s) => (
            <LegendKey key={s.key} color={s.color} label={s.label} />
          ))}
          <LegendKey color="var(--series-5)" label="Δ Net worth" shape="line" />
        </div>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={rows} margin={CHART_MARGIN} stackOffset="sign">
              <CartesianGrid {...gridProps} />
              <XAxis dataKey="label" {...xAxisProps} minTickGap={16} />
              <YAxis {...yAxisProps} tickFormatter={(v: number) => fmtUsdCompact(v)} />
              <Tooltip cursor={barCursor} content={<ChartTooltip />} />
              <ReferenceLine y={0} stroke="var(--baseline)" />
              {shown.map((s) => (
                <Bar key={s.key} dataKey={s.key} name={s.label} stackId="sv" fill={s.color} maxBarSize={BAR_MAX} radius={BAR_RADIUS} isAnimationActive={false} />
              ))}
              <Line type="monotone" dataKey="increase" name="Δ Net worth" stroke="var(--series-5)" strokeWidth={2} dot={{ r: 3 }} activeDot={ACTIVE_DOT} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card title="By year">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[60rem] text-sm">
            <thead className="text-left text-xs text-ink-3">
              <tr>
                <th className="py-1.5 pr-4 font-medium">Year</th>
                <th className="py-1.5 pr-4 text-right font-medium">Income</th>
                <th className="py-1.5 pr-4 text-right font-medium">Spending</th>
                <th className="py-1.5 pr-4 text-right font-medium">Saved income</th>
                {has('mortgagePrincipal') && <th className="py-1.5 pr-4 text-right font-medium">Mortgage principal</th>}
                {has('retirement') && <th className="py-1.5 pr-4 text-right font-medium">Retirement</th>}
                {has('rental') && <th className="py-1.5 pr-4 text-right font-medium">Rental property</th>}
                <th className="py-1.5 pr-4 text-right font-medium">Put in</th>
                {has('propertyMarks') && <th className="py-1.5 pr-4 text-right font-medium">Property marks</th>}
                <th className="py-1.5 pr-4 text-right font-medium">Market & other</th>
                <th className="py-1.5 pr-4 text-right font-medium">Δ Net worth</th>
                <th className="py-1.5 text-right font-medium">Capital share</th>
              </tr>
            </thead>
            <tbody>
              {byYearDesc.map((r) => (
                <tr key={r.year} className="border-t border-edge" data-year={r.year}>
                  <td className="py-1.5 pr-4 font-medium">{yearLabel(r)}</td>
                  <td className="py-1.5 pr-4 text-right tabular-nums">{fmtUsd(r.income)}</td>
                  <td className="py-1.5 pr-4 text-right tabular-nums">{fmtUsd(r.spending)}</td>
                  <td className={`py-1.5 pr-4 text-right tabular-nums ${signed(r.savedIncome)}`}>{fmtUsd(r.savedIncome)}</td>
                  {has('mortgagePrincipal') && <td className="py-1.5 pr-4 text-right tabular-nums">{r.mortgagePrincipal ? fmtUsd(r.mortgagePrincipal) : '—'}</td>}
                  {has('retirement') && <td className="py-1.5 pr-4 text-right tabular-nums">{r.retirement ? fmtUsd(r.retirement) : '—'}</td>}
                  {has('rental') && (
                    <td className="py-1.5 pr-4 text-right tabular-nums" title={`cash flow ${fmtUsd(r.rentalCashFlow)} + principal ${fmtUsd(r.rentalPrincipal)}`}>
                      {r.rental ? fmtUsd(r.rental) : '—'}
                    </td>
                  )}
                  <td className={`py-1.5 pr-4 text-right font-medium tabular-nums ${signed(r.saved)}`}>{fmtUsd(r.saved)}</td>
                  {has('propertyMarks') && <td className={`py-1.5 pr-4 text-right tabular-nums ${signed(r.propertyMarks)}`}>{r.propertyMarks ? fmtUsd(r.propertyMarks) : '—'}</td>}
                  <td className={`py-1.5 pr-4 text-right tabular-nums ${signed(r.marketOther ?? 0)}`}>{fmtUsd(r.marketOther ?? 0)}</td>
                  <td className={`py-1.5 pr-4 text-right font-medium tabular-nums ${signed(r.increase ?? 0)}`}>{fmtUsd(r.increase ?? 0)}</td>
                  <td className="py-1.5 text-right tabular-nums">{r.capitalShare == null ? '—' : fmtPct(r.capitalShare)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card title="How this is computed">
        <ul className="list-disc space-y-1 pl-5 text-sm text-ink-2">
          <li><strong>Saved income</strong> is income minus spending for the year, exactly as the Cash Flow page counts them. Paychecks are net pay, so pre-tax retirement deductions are added separately.</li>
          <li><strong>Mortgage principal</strong> is how much the home mortgage balance fell. Its payments are already inside spending, so this adds the principal back and leaves only the interest as a cost.</li>
          <li><strong>Retirement contributions</strong> are the employee contribution rows on the 401k accounts. Employer match arrives as balance, so it shows up under market &amp; other.</li>
          <li><strong>Rental property</strong> is the investment property as one unit: its rent and expenses, which the tag policy keeps out of saved income, plus its mortgage paydown. Hover the cell for the split.</li>
          <li><strong>Property marks</strong> are the valuation moves on real estate. A purchase itself nets against the cash and the new mortgage.</li>
          <li><strong>Market &amp; other</strong> is the remainder: brokerage, retirement and crypto moves, employer match, and anything untracked. Equity compensation is assumed to be zero.</li>
        </ul>
      </Card>
    </div>
  )
}
